import {
  Component,
  ViewChild,
  ElementRef,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { finalize } from 'rxjs/operators';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakRole } from '../../../models';
import { NotificationService } from '../../../services/notification.service';
import { PlanningExcelService } from '../../../shared/services';
import { PlanningExcelVersion } from '../../models';

@Component({
  selector: 'backoffice-planning-excel-upload-button',
  templateUrl: './planning-excel-upload-button.component.html',
  styleUrls: ['./planning-excel-upload-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PlanningExcelUploadButtonComponent implements OnInit {
  @ViewChild('file', { static: true }) file!: ElementRef<HTMLInputElement>;
  loading = false;
  versions = Object.values(PlanningExcelVersion);
  currentVersion: PlanningExcelVersion | null = null;

  userAllowedToUploadPlanningExcel = false;

  constructor(
    private readonly planningExcelService: PlanningExcelService,
    private readonly notificationService: NotificationService,
    private readonly keycloak: KeycloakService
  ) {}

  ngOnInit() {
    // only planner are allowed to upload a planning excel
    this.userAllowedToUploadPlanningExcel = this.keycloak.isUserInRole(
      KeycloakRole.BACKOFFICE_PLANNER
    );
  }

  promptExcel(version: PlanningExcelVersion) {
    this.file.nativeElement.click();
    this.currentVersion = version;
  }

  handleFileSelected() {
    const fileInput = this.file.nativeElement;
    const currentVersion = this.currentVersion;

    if (fileInput?.files?.length === 1 && currentVersion) {
      this.loading = true;
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const excel = fileInput.files.item(0)!;

      this.planningExcelService
        .validatePlanningExcel(excel, currentVersion)
        .pipe(
          finalize(() => {
            fileInput.value = '';
            this.loading = false;
          })
        )
        .subscribe({
          next: (validationResult: any) => {
            switch (validationResult.status) {
              case 'OK':
                this.finalizeUpload(excel, false);
                break;

              case 'WARNING':
                const messages = Array.from(
                  new Set(
                    validationResult.warnings.map(
                      (w: any) =>
                        `ATW-ID ${w.atwId} – PLZ geändert von ${w.oldZip} zu ${w.newZip}.\n`
                    )
                  )
                ).join('\n');

                this.notificationService
                  .openConfirmDialog({
                    title: 'Abweichende Postleitzahl gefunden',
                    content: `${messages}\nMöchten Sie den Import trotzdem fortsetzen?`,
                  })
                  .afterClosed()
                  .subscribe((confirmed: boolean) => {
                    if (confirmed) {
                      this.finalizeUpload(excel, true);
                    } else {
                      this.notificationService.infoMessage(
                        'Import abgebrochen.'
                      );
                    }
                    this.currentVersion = null;
                  });
                break;

              default:
                this.notificationService.errorMessage(
                  validationResult.message || 'Unbekannter Fehler beim Import.'
                );
                break;
            }
          },
          error: (error) => {
            this.notificationService.openNotificationDialog({
              title: 'Fehler',
              content:
                'Das Planungs-Excel konnte leider nicht korrekt verarbeitet werden.',
              error: error.error?.message || 'Unbekannter Fehler',
            });
          },
        });
    }
  }

  private finalizeUpload(excelFile: File, allowZipOrTypeMismatch: boolean) {
    if (!this.currentVersion) {
      this.notificationService.errorMessage(
        'Version nicht gesetzt. Upload abgebrochen.'
      );
      return;
    }

    this.loading = true;

    this.planningExcelService
      .uploadPlanningExcel(
        excelFile,
        this.currentVersion,
        allowZipOrTypeMismatch
      )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          this.notificationService.successMessage(
            'Planungs-Excel erfolgreich hochgeladen.'
          );
        },
        error: (error) => {
          this.notificationService.errorMessage(
            error.error?.message || 'Upload fehlgeschlagen'
          );
        },
      });
  }
}
